import { PraiseListResponseSchema } from '@cult/schemas';
import { keepPreviousData, useInfiniteQuery, useQuery } from '@tanstack/react-query';
import type { CreatedRangeParams } from '../../../components';
import { apiFetch } from '../../../lib/api-client';

export type PraiseFilters = CreatedRangeParams & {
  coreValue?: string;
  coreValues?: string[];
  frameworks?: string[];
  author?: string;
  receiver?: string;
  q?: string;
};

export const buildPraiseParams = (filters: PraiseFilters): URLSearchParams => {
  const params = new URLSearchParams();
  if (filters.coreValue) params.set('coreValue', filters.coreValue);
  // Multiselect → repeated params (?coreValues=a&coreValues=b), matching the API's array parser.
  for (const id of filters.coreValues ?? []) params.append('coreValues', id);
  for (const id of filters.frameworks ?? []) params.append('frameworks', id);
  if (filters.author) params.set('author', filters.author);
  if (filters.receiver) params.set('receiver', filters.receiver);
  if (filters.q) params.set('q', filters.q);
  if (filters.createdFrom) params.set('createdFrom', filters.createdFrom);
  if (filters.createdTo) params.set('createdTo', filters.createdTo);
  return params;
};

export const praisesKey = (filters: PraiseFilters) => ['praises', filters] as const;

export const usePraises = (filters: PraiseFilters = {}, options?: { enabled?: boolean }) => {
  return useInfiniteQuery({
    queryKey: praisesKey(filters),
    enabled: options?.enabled ?? true,
    queryFn: async ({ pageParam, signal }: { pageParam: string | undefined; signal: AbortSignal }) => {
      const params = buildPraiseParams(filters);
      if (pageParam) params.set('cursor', pageParam);
      const search = params.toString();
      const raw = await apiFetch<unknown>(`/praises${search ? `?${search}` : ''}`, { signal });
      return PraiseListResponseSchema.parse(raw);
    },
    initialPageParam: undefined as string | undefined,
    getNextPageParam: (last) => last.nextCursor ?? undefined,
    placeholderData: keepPreviousData,
  });
};

export const praisesPagedKey = (filters: PraiseFilters, page: number, pageSize: number) =>
  ['praises', 'paged', filters, page, pageSize] as const;

export const usePraisesPaged = (filters: PraiseFilters, page: number, pageSize: number) => {
  const offset = (page - 1) * pageSize;
  return useQuery({
    queryKey: praisesPagedKey(filters, page, pageSize),
    queryFn: async ({ signal }: { signal: AbortSignal }) => {
      const params = buildPraiseParams(filters);
      params.set('limit', String(pageSize));
      params.set('offset', String(offset));
      const raw = await apiFetch<unknown>(`/praises?${params.toString()}`, { signal });
      return PraiseListResponseSchema.parse(raw);
    },
    placeholderData: keepPreviousData,
  });
};
